import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { authService } from '@/lib/supabase/auth';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      // Validar email
      if (!email.trim()) {
        throw new Error('Por favor, insira seu email');
      }

      // Enviar email de recuperação de senha
      await authService.resetPassword(email);

      // Sucesso
      setSuccess(true);
      toast({
        title: "Email enviado",
        description: "Verifique sua caixa de entrada para instruções de recuperação de senha",
      });
    } catch (error: any) {
      console.error('Erro ao enviar email de recuperação:', error);
      
      setError(error.message || 'Ocorreu um erro ao enviar o email de recuperação');
      toast({
        title: "Erro",
        description: error.message || 'Não foi possível enviar o email de recuperação',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-voah-cream p-4 sm:p-6 lg:p-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/lovable-uploads/3aedb010-4564-4186-8687-ad2d8f6d7160.png"
              alt="VOAH Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold">Recuperar Senha</CardTitle>
          <CardDescription>
            Enviaremos instruções para redefinir sua senha
          </CardDescription>
        </CardHeader>
        
        {success ? (
          <div className="p-6 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h3 className="mb-2 text-lg font-medium">Email enviado!</h3>
            <p className="mb-6 text-sm text-gray-600">
              Enviamos instruções para recuperação de senha para {email}.<br />
              Verifique sua caixa de entrada e spam.
            </p>
            <Button
              type="button"
              className="bg-voah-teal hover:bg-voah-teal-dark w-full"
              onClick={() => navigate('/admin/login')}
            >
              Voltar para o login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleResetPassword}>
            <CardContent className="space-y-4">
              {error && (
                <div className="p-3 text-sm rounded-md bg-red-50 text-red-600 border border-red-200">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-3">
              <Button 
                type="submit" 
                className="w-full bg-voah-teal hover:bg-voah-teal-dark"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar instruções"
                )}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full text-gray-600"
                onClick={() => navigate('/admin/login')}
                disabled={isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar para o login
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
} 